<template>
    <div id="invoices-client-page">
        <div class="container-fluid py-4">
            <div class="d-flex justify-content-between align-items-center mb-4">
                <h1 class="h3 mb-0 text-gray-800">{{ t('invoices-my_invoices') }}</h1>
            </div>

            <div v-if="filtersReady" class="mb-3 invoices-filter-bar">
                <KvFilter
                    :defaultSelected="['period']"
                    ref="filterBar"
                    @changed="updateFilters"
                    :show-labels="true"
                    :has-period-filter="false"
                    :has-branch-filter="false"
                    :filters="extraFilters"
                />
                <div class="clearfix"></div>
            </div>

            <div class="row main-row">
                <div class="col-12">
                    <div class="card shadow-sm border-0">
                        <div class="card-body p-0">
                            <grid
                                :source="'invoices'"
                                :filters="filters"
                                :showSearch="false"
                                ref="invoicesGrid"
                            >
                                <template v-slot:rows="grid">
                                    <thead>
                                        <tr>
                                            <grid-th :grid="grid">{{ t('invoices-invoice') }}</grid-th>
                                            <grid-th :grid="grid">{{ t('invoices-issue_date') }}</grid-th>
                                            <grid-th :grid="grid">{{ t('invoices-due_date') }}</grid-th>
                                            <grid-th :grid="grid">{{ t('invoices-total_amount') }}</grid-th>
                                            <grid-th :grid="grid" v-if="isMatrixUser">{{ t('invoices-branch') }}</grid-th>
                                            <grid-th :grid="grid">{{ t('invoices-status_label') }}</grid-th>
                                            <grid-th :grid="grid" class="text-left">{{ t('invoices-actions') }}</grid-th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="invoice in (grid.data || [])" :key="invoice.id">
                                            <td class="invoice-title-cell">
                                                <span class="font-weight-500 text-dark invoice-title-text">{{ invoice.title || invoice.id }}</span>
                                                <div class="text-muted small mt-1" v-if="invoice.billing_cycle_start">
                                                    {{ formatMonthYear(invoice.billing_cycle_start) }}
                                                </div>
                                            </td>
                                            <td>
                                                <human-date :date="invoice.created_at" format="[d]/[M]/[Y]" />
                                            </td>
                                            <td>
                                                <human-date :date="invoice.due_date" format="[d]/[M]/[Y]" />
                                            </td>
                                            <td>
                                                <div class="font-weight-500">{{ formatCurrency(invoice.total) }}</div>
                                                <div v-if="invoice.total_paid !== null && Number(invoice.total_paid).toFixed(2) !== Number(invoice.total).toFixed(2)"
                                                     class="mt-1" style="font-size: 11px; color: #6B7280;">
                                                    <span class="text-success font-weight-500 mr-1">{{ t('invoices-paid_label') }}</span>
                                                    {{ formatCurrency(invoice.total_paid) }}
                                                </div>
                                            </td>
                                            <td v-if="isMatrixUser">
                                                <span class="text-muted small">{{ invoice.place_branch_title || '—' }}</span>
                                            </td>
                                            <td>
                                                <invoice-status-badge :status="invoice.status" :due-date="invoice.due_date" />
                                            </td>
                                            <td class="text-left">
                                                <div class="d-flex align-items-center gap-2">
                                                    <kv-button
                                                        v-if="canPay(invoice)"
                                                        variant="filled"
                                                        size="small"
                                                        :prepend-icon="getPayButtonIcon(invoice)"
                                                        @click="payInvoice(invoice)"
                                                    >
                                                        {{ t('invoices-pay') }}
                                                    </kv-button>
                                                    <invoice-actions-menu
                                                        :invoice="invoice"
                                                        :isAdmin="false"
                                                        @view="goToDetails"
                                                        @pay="payInvoice"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                        <tr v-if="!grid.loading && (!grid.data || grid.data.length === 0)">
                                            <td :colspan="isMatrixUser ? 7 : 6" class="empty-state-cell">
                                                <div class="empty-state">
                                                    <div class="empty-state-icon">
                                                        <i class="fa fa-file-text-o"></i>
                                                    </div>
                                                    <h5 class="empty-state-title">{{ t('invoices-no_invoices_found') }}</h5>
                                                    <p class="empty-state-subtitle">{{ t('invoices-no_invoices_subtitle') }}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </grid>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <invoice-payment-method-modal
            ref="paymentMethodModal"
            :invoice="selectedInvoiceForPayment"
        />
    </div>
</template>

<script>
import KvFilter from "../components/reports/kv-filter-bar.vue";
import InvoiceStatusBadge from "./components/invoice-status-badge.vue";
import InvoiceActionsMenu from "./components/invoice-actions-menu.vue";
import InvoicePaymentMethodModal from "./components/invoice-payment-method-modal.vue";
import filtersMixin from "./helpers/filters-mixin";
import { formatCurrency, formatMonthYear } from "./helpers";

export default {
    name: "invoices-client",
    mixins: [filtersMixin],
    components: {
        KvFilter,
        InvoiceStatusBadge,
        InvoiceActionsMenu,
        InvoicePaymentMethodModal
    },
    created() {
        const permissions = this.$store.state.user?.permissions || {};
        const hasPermission = permissions.invoices?.enabled === 1;

        if (!hasPermission) {
            this.$router.replace('/');
            return;
        }
    },
    data() {
        return {
            selectedInvoiceForPayment: null,
            extraFilters: [
                {
                    component: "period",
                    label: this.t('invoices-filter_by_date'),
                    name: "period",
                    removable: false,
                    options: { maxRangeMonths: 12 }
                },
                {
                    component: "checkbox",
                    label: this.t('invoices-status_label'),
                    name: "status",
                    removable: true,
                    options: {
                        icon: "fa-tag",
                        options: [
                            { value: "pending", label: this.t('invoices-status_pending') },
                            { value: "overdue", label: this.t('invoices-status_overdue') },
                            { value: "paid", label: this.t('invoices-status_paid') },
                            { value: "cancelled", label: this.t('invoices-status_cancelled') }
                        ]
                    }
                }
            ]
        };
    },
    computed: {
        isMatrixUser() {
            return !this.$store.state.user.places_branches_id;
        },
        isAdmin() {
            return !!this.$store?.state?.user?.global_admin;
        }
    },
    methods: {
        formatCurrency,
        formatMonthYear,
        goToDetails(invoice) {
            this.$router.push(`/invoices/${invoice.id}`);
        },
        canPay(invoice) {
            if (!invoice) return false;
            if (invoice.status === 'paid' || invoice.status === 'cancelled') return false;
            return !!(invoice.bank_slip_url || invoice.payment_link_url);
        },
        getPayButtonIcon(invoice) {
            if (invoice && invoice.payment_link_url && !invoice.bank_slip_url) {
                return 'ui-credit-card-refresh';
            }
            return 'ui-bank-note-01';
        },
        payInvoice(invoice) {
            if (!invoice) return;
            const hasSlip = !!invoice.bank_slip_url;
            const hasLink = !!invoice.payment_link_url;

            if (hasSlip && hasLink) {
                this.selectedInvoiceForPayment = invoice;
                this.$nextTick(() => {
                    if (this.$refs.paymentMethodModal) {
                        this.$refs.paymentMethodModal.open();
                    }
                });
            } else if (hasLink) {
                window.open(invoice.payment_link_url, "_blank");
            } else if (hasSlip) {
                window.open(invoice.bank_slip_url, "_blank");
            } else {
                this.$store.dispatch("notification/warning", this.t('invoices-payment_unavailable_msg'));
            }
        }
    }
};
</script>

<style lang="scss" scoped>
#invoices-client-page {
    .invoices-filter-bar {
        background: transparent !important;
        border: none !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        
        ::v-deep .kv-filters {
            background: transparent !important;
            padding: 0 !important;
        }
    }

    .card {
        border-radius: 12px;
        overflow: hidden;
    }

    .invoice-title-cell {
        max-width: 220px;
    }
    .invoice-title-text {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 200px;
    }

    .empty-state-cell {
        padding: 0 !important;
        border: none !important;
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 24px;
        text-align: center;

        .empty-state-icon {
            width: 72px;
            height: 72px;
            border-radius: 50%;
            background: #F9FAFB;
            border: 1px solid #E5E7EB;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 16px;

            i {
                font-size: 28px;
                color: #9CA3AF;
            }
        }

        .empty-state-title {
            font-size: 16px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 6px;
        }

        .empty-state-subtitle {
            font-size: 13px;
            color: #9CA3AF;
            margin: 0;
            max-width: 300px;
        }
    }
}
</style>
